import { useState } from 'react';
import { Input } from '@/components/_untitled/base/input/input';
import { Radio, RadioGroup } from 'react-aria-components';
import { Check } from '@untitledui/icons';
import { cx } from '@/utils/cx';
import type { KycStepProps } from '../../config/kycSteps.config';
import { StepFooter } from '../StepFooter';
import { prefixes, useTypedTranslation } from "@/locales";

export function PersonalInfoStep({ data, onComplete, onBack }: KycStepProps) {
    const [tGeneral, tPersonalInfo] = useTypedTranslation(prefixes('general', 'kyc.steps.personalInfo'));
    const [firstName, setFirstName] = useState(data.firstName || '');
    const [lastName, setLastName] = useState(data.lastName || '');
    const [biddingType, setBiddingType] = useState<'INDIVIDUAL' | 'COMPANY'>(
        data.biddingType || 'INDIVIDUAL'
    );
    const [companyName, setCompanyName] = useState(data.companyName || '');
    const [phone, setPhone] = useState(data.phone || '');
    const [whatsapp, setWhatsapp] = useState(data.whatsapp || '');
    const [taxId, setTaxId] = useState(data.taxId || '');

    const canProceed =
        !!firstName &&
        !!lastName &&
        (biddingType === 'INDIVIDUAL' || (biddingType === 'COMPANY' && !!companyName));

    const handleContinue = () => {
        onComplete({
            firstName,
            lastName,
            biddingType,
            companyName: biddingType === 'COMPANY' ? companyName : null,
            phone: phone || null,
            whatsapp: whatsapp || null,
            taxId: biddingType === 'COMPANY' ? (taxId || null) : null,
        });
    };

    return (
        <>
            <Input
                label={ tPersonalInfo('firstName') }
                value={ firstName }
                onChange={ (value) => setFirstName(value) }
                aria-label={ tPersonalInfo('firstName') }
                isRequired
            />
            <Input
                label={ tPersonalInfo('lastName') }
                value={ lastName }
                onChange={ (value) => setLastName(value) }
                aria-label={ tPersonalInfo('lastName') }
                isRequired
            />

            <Input
                label={ tPersonalInfo('phone') }
                placeholder={ tPersonalInfo('phonePlaceholder') }
                value={ phone }
                onChange={ (value) => setPhone(value) }
                aria-label={ tPersonalInfo('phone') }
            />

            <Input
                label={ tPersonalInfo('whatsapp') }
                placeholder={ tPersonalInfo('whatsappPlaceholder') }
                value={ whatsapp }
                onChange={ (value) => setWhatsapp(value) }
                aria-label={ tPersonalInfo('whatsapp') }
            />

            <div className="flex flex-col gap-5">
                <p className="text-md text-tertiary">{ tPersonalInfo('biddingType') }</p>
                <RadioGroup
                    value={ biddingType }
                    onChange={ (value) => setBiddingType(value as 'INDIVIDUAL' | 'COMPANY') }
                    aria-label={ tPersonalInfo('biddingType') }
                    className="flex gap-5"
                >
                    { (['INDIVIDUAL', 'COMPANY'] as const).map((type) => (
                        <Radio
                            key={ type }
                            value={ type }
                            className={ ({ isSelected }) =>
                                cx(
                                    'relative flex-1 cursor-pointer rounded-xl border-2 bg-primary p-5 transition-colors',
                                    isSelected ? 'border-brand-950' : 'border-secondary_alt',
                                )
                            }
                        >
                            { ({ isSelected }) => (
                                <>
                                    <div className="flex flex-col gap-2">
                                        <p className="text-md font-semibold text-primary">
                                            { tPersonalInfo(type.toLowerCase() as 'company' | 'individual') }
                                        </p>
                                        <p className="text-sm text-tertiary">
                                            { tPersonalInfo(`${ type.toLowerCase() as 'company' | 'individual' }Description`) }
                                        </p>
                                    </div>
                                    <div
                                        className={ cx(
                                            'absolute top-4 end-4 flex size-5 items-center justify-center rounded-md border transition-colors',
                                            isSelected ? 'border-brand-950 bg-brand-950' : 'border-secondary_alt bg-primary',
                                        ) }
                                    >
                                        { isSelected && <Check className="size-3 text-white"/> }
                                    </div>
                                </>
                            ) }
                        </Radio>
                    )) }
                </RadioGroup>
            </div>

            { biddingType === 'COMPANY' && (
                <>
                    <Input
                        label={ tPersonalInfo('companyName') }
                        value={ companyName }
                        onChange={ (value) => setCompanyName(value) }
                        aria-label={ tPersonalInfo('companyName') }
                        isRequired
                    />
                    <Input
                        label={ tPersonalInfo('taxId') }
                        value={ taxId }
                        onChange={ (value) => setTaxId(value) }
                        aria-label={ tPersonalInfo('taxId') }
                    />
                </>
            ) }

            <StepFooter
                primary={ {
                    label: tGeneral('continue'),
                    onClick: handleContinue,
                    disabled: !canProceed
                } }
                back={ { show: true, onClick: onBack } }
            />
        </>
    );
}
